/**
 * Puter Basic app embed helpers for grudge-poker.
 *
 * SSOT: Puter app index_url → https://poker.grudge-studio.com/ (same SPA as CF).
 * Never a second ledger or wallet on Puter KV.
 *
 * @see docs/PUTER_GRUDGE_POKER.md
 * @see docs/PHANTOM_EMBEDDED_SSOT.md
 */

import { FLEET } from "@shared/fleet";

export const PUTER_POKER = {
  appName: FLEET.puterPokerAppName,
  appId: FLEET.puterPokerAppId,
  appUrl: FLEET.puterPokerApp,
  indexUrl: FLEET.publicOrigin + "/",
  productionOrigin: FLEET.publicOrigin,
} as const;

/** True when this document is framed (Puter desktop window, etc.). */
export function isFramed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent blocks access — still framed
    return true;
  }
}

/**
 * Best-effort detect Puter parent (desktop / puter.com/app/*).
 * Referrer + ancestorOrigins (Chrome) — never trust for auth, only UX.
 */
export function isPuterEmbed(): boolean {
  if (typeof window === "undefined") return false;
  if (!isFramed()) {
    // Top-level puter.com rarely hosts our SPA; index_url is poker host
    return false;
  }
  try {
    const ancestors = (location as Location & { ancestorOrigins?: DOMStringList })
      .ancestorOrigins;
    if (ancestors && ancestors.length) {
      for (let i = 0; i < ancestors.length; i++) {
        const a = ancestors[i] || "";
        if (/puter\.com/i.test(a) || /puter\.site/i.test(a)) return true;
      }
    }
  } catch {
    /* ignore */
  }
  try {
    if (document.referrer && /puter\.com|puter\.site/i.test(document.referrer)) {
      return true;
    }
  } catch {
    /* ignore */
  }
  // Unknown cross-origin parent + framed → treat as embed shell for wallet breakout
  return isFramed();
}

/**
 * Canonical production URL for path (always poker host for Phantom allowlist).
 */
export function productionUrlForPath(path?: string): string {
  let p =
    path ||
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "/");
  if (!p.startsWith("/")) p = `/${p}`;
  return `${FLEET.publicOrigin}${p}`;
}

/**
 * Phantom OAuth + extension inject are most reliable top-level on the allowlisted
 * origin. When framed by Puter, break out before google/apple connect or when
 * injected provider is missing.
 */
export function breakoutToProduction(path?: string): void {
  if (typeof window === "undefined") return;
  const target = productionUrlForPath(path);
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = target;
      return;
    }
  } catch {
    /* parent blocked assignment */
  }
  window.location.assign(target);
}

/**
 * Before Phantom embedded OAuth (google/apple): if framed, leave Puter iframe
 * so redirectUrl lands on a top-level poker.grudge-studio.com/connect session.
 * Returns true if a breakout navigation was started (caller should abort connect).
 */
export function ensureTopLevelForPhantomOAuth(): boolean {
  if (!isFramed()) return false;
  try {
    sessionStorage.setItem("phantom_oauth_pending", "1");
    sessionStorage.setItem("puter_breakout_oauth", "1");
  } catch {
    /* ignore */
  }
  breakoutToProduction("/connect");
  return true;
}

/**
 * Optional Puter.js load for User-Pays identity badge only — never play GBUX.
 * Returns null if script blocked or offline.
 */
export async function loadPuterJsOptional(): Promise<
  { auth?: { isSignedIn?: () => boolean; getUser?: () => Promise<unknown> } } | null
> {
  if (typeof window === "undefined") return null;
  const w = window as Window & { puter?: unknown };
  if (w.puter) return w.puter as { auth?: { isSignedIn?: () => boolean } };
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://js.puter.com/v2/";
    s.async = true;
    s.onload = () =>
      resolve(
        (window as Window & { puter?: { auth?: { isSignedIn?: () => boolean } } })
          .puter || null,
      );
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}
